import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import ConfirmModal, { ConfirmModalHeader } from "PFComponents/confirm/confirm_modal";
import { LoadingDots } from "PFComponents/loading_dots";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import ProfilesAvatarStack from "PFComponents/profile_avatar/profiles_avatar_stack";
import { getProfileName } from "PFCore/helpers/profile";
import useInsightsApi from "PFCore/helpers/use_insights_api";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { fetchProfilesList } from "../../services/api";
import PiePreview from "../previews/pie_preview";
import css from "./roles_list_modal.module.scss";
import TimeToAction from "./time_to_action";
import TimeToActionLegend from "./time_to_action_legend";

const RolesListModal = () => {
  const {
    store: {
      pages: {
        insights: { modalData, setModalData, selectedFilters }
      }
    }
  } = useContext(AppContext);

  const isOpen = Boolean(modalData);

  const { t } = useTranslation("insights");
  const { insightsApi, insightsApiError } = useInsightsApi();
  const growl = useGrowl();

  const [loading, setLoading] = useState(true);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      insightsApi("/roles_list", {
        ...selectedFilters,
        ...modalData,
        page: page,
        per_page: 10,
        chart_type: modalData?.chartType
      })
        .then((res) => {
          setData(res);
          setLoading(false);
          const profilesList = res?.entries?.map((item) => item?.profile_id).flatMap((item) => item);
          if (profilesList) {
            fetchProfiles(profilesList);
          }
        })
        .catch(() => {
          setLoading(false);
          setModalData(null);
          growl({
            message: t("error"),
            kind: "error"
          });
        });
    }
  }, [JSON.stringify(modalData), page]);

  useEffect(() => {
    if (!isOpen) {
      return () => {
        setPage(1);
      };
    }
  }, [isOpen]);

  const fetchProfiles = async (profilesList) => {
    await fetchProfilesList(profilesList)
      .then((res) => {
        setProfiles(res.entries);
        setLoadingProfiles(false);
      })
      .catch(() => setLoadingProfiles(false));
  };

  const handleAvatarClick = (profile) => {
    window.open(`/profiles/${profile.id}`, "_blank");
  };

  const profileAvatars = (role) =>
    loadingProfiles ? (
      <LoadingDots />
    ) : (
      <ProfilesAvatarStack
        profiles={profiles
          .filter((profile) => role?.profile_id?.includes(profile.id))
          .map((item) => item.profile)}
        max={3}
        avatarSize={30}
        handleAvatarClick={handleAvatarClick}
        getTooltipContent={(profile) => getProfileName(profile)}
      />
    );

  const hasLegend = modalData?.chartType === "roles_hist";

  if (!isOpen || !data) {
    return null;
  }

  return (
    <ConfirmModal handleClose={() => setModalData(null)} showOKButton={false} labelCancel={"close"}>
      {insightsApiError || loading ? (
        <PiePreview animated content={t("loading")} />
      ) : (
        <>
          <ConfirmModalHeader>{modalData.title}</ConfirmModalHeader>
          <div className={css.wrapper}>
            {data.entries.map((role) => (
              <div className={css.line} key={role.task_id}>
                <Link className={css.role} to={`/activities/${role.task_id}`}>
                  <div className={css.taskId}>{`${role.task_id} -`}</div>
                  <div className={css.name} title={role.name || role.task_name}>
                    {role.name || role.task_name}
                  </div>
                </Link>
                {modalData.parent === "reasons" && profileAvatars(role)}
                {modalData.parent === "hist" && <TimeToAction role={role} />}
              </div>
            ))}
          </div>
          <div className={classNames(css.pagination, { [css.hasLegend]: hasLegend })}>
            <SimplePagination
              currentPage={page}
              handleClick={setPage}
              totalPages={data?.meta?.total_page}
              //2 and 3 to make pagination not to break into two lines when there is legend visible
              customSpan={hasLegend ? 2 : 3}
            />
            {modalData.parent === "hist" && <TimeToActionLegend />}
          </div>
        </>
      )}
    </ConfirmModal>
  );
};

export default RolesListModal;
